/**
 * SetupProgressStep - Loading screen shown while provisioning a sample dataset.
 * Animates through a checklist and waits for both the provision API call and a
 * minimum display time (5-10 s random) before navigating forward.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, LinearProgress, Stack, alpha,
} from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded';
import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import EastRounded from '@mui/icons-material/EastRounded';
import { brandPalette } from '../../../theme/brandPalette';
import OnboardingLayout from '../components/OnboardingLayout';
import { config } from '../../../config';

interface SetupProgressStepProps {
  onComplete: () => void;
  provisionPromise?: Promise<any>;
}

const SAMPLE_STEPS = [
  { label: 'Setting up your workspace', durationMs: 800 },
  { label: 'Loading real business data', durationMs: 1200 },
  { label: 'Mapping your data structure', durationMs: 1000 },
  { label: 'Connecting the dots across your data', durationMs: 1200 },
  { label: 'Crafting your first insights', durationMs: 800 },
];

const DID_YOU_KNOW = [
  `${config.brandName} reads your schema once and never stores your raw data.`,
  'You can ask follow-up questions on any chart - just keep typing.',
  'Every answer includes the SQL query it was generated from.',
  'Pin any insight to a dashboard with a single click.',
  `${config.brandName} works with Snowflake, BigQuery, Redshift, Postgres & MySQL.`,
  'You can summarize an entire dashboard as an AI-written report.',
];

export default function SetupProgressStep({ onComplete, provisionPromise }: SetupProgressStepProps) {
  const [fakeStep, setFakeStep] = useState(0);
  const [fakeProgress, setFakeProgress] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const [tipVisible, setTipVisible] = useState(true);

  const minWaitMs = useRef(5000 + Math.floor(Math.random() * 5001));

  // Animate steps across 90% of the minimum wait
  useEffect(() => {
    const totalOriginalMs = SAMPLE_STEPS.reduce((sum, s) => sum + s.durationMs, 0);
    const scale = (minWaitMs.current * 0.9) / totalOriginalMs;
    let elapsed = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];
    SAMPLE_STEPS.forEach((step, idx) => {
      timers.push(setTimeout(() => {
        setFakeStep(idx + 1);
        setFakeProgress(Math.min(95, Math.round(((idx + 1) / SAMPLE_STEPS.length) * 100)));
      }, elapsed));
      elapsed += Math.round(step.durationMs * scale);
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  // Cycle tips with a fade effect every 3s
  useEffect(() => {
    const interval = setInterval(() => {
      setTipVisible(false);
      setTimeout(() => {
        setTipIndex(i => (i + 1) % DID_YOU_KNOW.length);
        setTipVisible(true);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Wait for both provision API and minimum display time
  useEffect(() => {
    if (!provisionPromise) return;
    const minWaitPromise = new Promise<void>(resolve => setTimeout(resolve, minWaitMs.current));
    Promise.all([provisionPromise, minWaitPromise]).then(() => {
      setFakeProgress(100);
      setFakeStep(SAMPLE_STEPS.length);
      setTimeout(() => onComplete(), 700);
    }).catch(() => {});
  }, [provisionPromise, onComplete]);

  return (
    <OnboardingLayout maxWidth={480}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, letterSpacing: -0.5 }}>
          Your workspace is being prepared
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sit tight - we're getting everything ready for you.
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: 3.5 }}>
        <LinearProgress
          variant="determinate"
          value={fakeProgress}
          sx={{
            height: 6,
            borderRadius: 4,
            bgcolor: alpha(brandPalette.primary[400], 0.12),
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: `linear-gradient(90deg, ${brandPalette.primary[300]}, ${brandPalette.primary[400]})`,
              transition: 'transform 0.6s ease',
            },
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, textAlign: 'right', fontSize: '0.75rem' }}>
          {fakeProgress}%
        </Typography>
      </Box>

      {/* Steps card */}
      <Box sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(brandPalette.primary[400], 0.15),
        bgcolor: 'background.paper',
        overflow: 'hidden',
        mb: 2.5,
      }}>
        <Stack>
          {SAMPLE_STEPS.map((step, idx) => {
            const status = idx < fakeStep ? 'completed' : idx === fakeStep ? 'running' : 'pending';
            const isRunning = status === 'running';
            return (
              <Box
                key={idx}
                sx={{
                  px: 2.5, py: 1.5,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  borderBottom: idx < SAMPLE_STEPS.length - 1 ? '1px solid' : 'none',
                  borderColor: alpha(brandPalette.primary[400], 0.08),
                  bgcolor: isRunning ? alpha(brandPalette.primary[400], 0.05) : 'transparent',
                  transition: 'background 0.3s ease',
                  ...(isRunning && {
                    boxShadow: `inset 3px 0 0 ${brandPalette.primary[400]}`,
                  }),
                }}
              >
                {/* Icon */}
                {status === 'completed' && (
                  <CheckCircleRounded sx={{
                    color: '#16a34a', fontSize: 18, flexShrink: 0,
                    animation: 'popIn 0.25s ease',
                    '@keyframes popIn': {
                      '0%': { transform: 'scale(0)', opacity: 0 },
                      '70%': { transform: 'scale(1.2)' },
                      '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                  }} />
                )}
                {status === 'running' && (
                  <AutorenewRounded sx={{
                    color: brandPalette.primary[400], fontSize: 18, flexShrink: 0,
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                  }} />
                )}
                {status === 'pending' && (
                  <RadioButtonUncheckedRounded sx={{ color: alpha('#000', 0.18), fontSize: 18, flexShrink: 0 }} />
                )}

                <Typography variant="body2" sx={{
                  fontWeight: isRunning ? 600 : status === 'completed' ? 500 : 400,
                  color: status === 'completed' ? 'text.primary' : isRunning ? brandPalette.primary[400] : 'text.disabled',
                  transition: 'color 0.2s',
                }}>
                  {step.label}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* Did you know tip */}
      <Box sx={{
        px: 2, py: 1.5,
        borderRadius: 2,
        bgcolor: alpha(brandPalette.primary[400], 0.06),
        border: '1px solid',
        borderColor: alpha(brandPalette.primary[400], 0.12),
        mb: 3,
        minHeight: 52,
        display: 'flex', alignItems: 'center',
      }}>
        <Typography variant="body2" sx={{
          color: 'text.secondary', fontSize: '0.78rem',
          opacity: tipVisible ? 1 : 0,
          transition: 'opacity 0.3s ease',
          lineHeight: 1.5,
        }}>
          <Box component="span" sx={{ fontWeight: 700, color: brandPalette.primary[400] }}>Did you know? </Box>
          {DID_YOU_KNOW[tipIndex]}
        </Typography>
      </Box>

      {/* Sneak peek */}
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
        <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.78rem' }}>
          You'll be asking your first question in just a moment
        </Typography>
        <EastRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
      </Stack>
    </OnboardingLayout>
  );
}
